<script setup>
/**
 * KaptionAiTakeoverStrip — ai-takeover.COMPOSER.1 / .TAKEOVER.1
 *
 * Sits on top of the reply box while the Kaption assistant is handling the conversation
 * (pending, or assigned to an assistant). The composer stays in private-note mode
 * underneath; this strip says who is answering and offers "Tomar el control": one click,
 * no confirmation. It assigns the conversation to the person and the assistant stops.
 *
 * NOTICE: KAPTION-INTEGRATION
 */
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { useFeaturePermissions } from 'dashboard/composables/useFeaturePermissions';
import NextButton from 'dashboard/components-next/button/Button.vue';
import KaptionAiTakeoverAPI from 'dashboard/api/kaptionAiTakeover';
import types from 'dashboard/store/mutation-types';
import { isKaptionAssistantUser } from 'dashboard/store/modules/conversations/helpers';
// NOTICE: KAPTION-INTEGRATION - Asistentes: "Agentforce" label next to the assistant name
import { useKaptionAssistantLabels } from 'dashboard/composables/useKaptionAssistantLabels';

const props = defineProps({
  conversation: { type: Object, required: true },
});

const emit = defineEmits(['taken']);

const { t } = useI18n();
const store = useStore();
const { canUseFeature } = useFeaturePermissions();
const currentUser = useMapGetter('getCurrentUser');

const isTaking = ref(false);

// NOTICE: KAPTION-INTEGRATION - Asistentes: provider label (null for Kaption assistants)
const { providerLabelFor } = useKaptionAssistantLabels();
const providerLabel = computed(() =>
  providerLabelFor(props.conversation?.meta?.assignee?.id)
);

const assistantName = computed(() => {
  const assignee = props.conversation?.meta?.assignee;
  return isKaptionAssistantUser(assignee) && assignee.name
    ? assignee.name
    : t('KAPTION_PLATFORM.AI_TAKEOVER.ASSISTANT_FALLBACK');
});

// ai-takeover.PERMISSIONS.1 — taking over is self-assignment.
const canTakeOver = computed(() => canUseFeature('direct_assignment'));

function applyLocally({ status, assignee }) {
  const conversationId = props.conversation.id;
  const { avatar_url: avatarUrl, ...rest } = currentUser.value || {};
  const owner =
    assignee && assignee.id === currentUser.value?.id
      ? { ...rest, thumbnail: avatarUrl }
      : assignee;
  store.dispatch('setCurrentChatAssignee', owner);
  if (status) {
    store.commit(types.CHANGE_CONVERSATION_STATUS, { conversationId, status });
  }
}

async function takeOver() {
  if (isTaking.value || !canTakeOver.value) return;
  isTaking.value = true;
  try {
    const { data } = await KaptionAiTakeoverAPI.take(props.conversation.id);
    applyLocally(data);
    emit('taken');
  } catch (error) {
    const owner = error?.response?.data?.owner;
    if (error?.response?.status === 409) {
      // ai-takeover.TAKEOVER.2 — someone else won; show who and let the list catch up.
      if (owner) applyLocally({ assignee: owner, status: 'open' });
      useAlert(
        owner?.name
          ? t('KAPTION_PLATFORM.AI_TAKEOVER.LOST_RACE', { name: owner.name })
          : t('KAPTION_PLATFORM.AI_TAKEOVER.ALREADY_RELEASED')
      );
    } else {
      useAlert(t('KAPTION_PLATFORM.AI_TAKEOVER.ERROR'));
    }
  } finally {
    isTaking.value = false;
  }
}
</script>

<template>
  <div
    data-testid="kaption-ai-takeover-strip"
    class="flex flex-wrap items-center gap-2 px-3 py-2 mx-2 mb-2 rounded-lg bg-n-iris-2 outline outline-1 outline-n-iris-6"
  >
    <span
      class="i-lucide-sparkles size-4 shrink-0 text-n-iris-10 animate-pulse"
    />
    <div class="flex-1 min-w-0">
      <p class="text-sm font-medium truncate text-n-slate-12">
        {{
          t('KAPTION_PLATFORM.AI_TAKEOVER.ANSWERING', { name: assistantName })
        }}
        <!-- NOTICE: KAPTION-INTEGRATION - Asistentes provider label -->
        <span
          v-if="providerLabel"
          data-testid="kaption-ai-takeover-provider"
          class="font-normal text-n-slate-11"
        >
          · {{ providerLabel }}
        </span>
      </p>
      <p class="text-xs text-n-slate-11">
        {{
          canTakeOver
            ? t('KAPTION_PLATFORM.AI_TAKEOVER.NOTES_ONLY')
            : t('KAPTION_PLATFORM.AI_TAKEOVER.NOTES_ONLY_NO_PERMISSION')
        }}
      </p>
    </div>
    <NextButton
      v-if="canTakeOver"
      data-testid="kaption-ai-takeover-button"
      :label="t('KAPTION_PLATFORM.AI_TAKEOVER.TAKE_OVER')"
      icon="i-lucide-hand"
      sm
      solid
      teal
      :is-loading="isTaking"
      :disabled="isTaking"
      @click="takeOver"
    />
  </div>
</template>
